import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Eye } from "lucide-react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  FormActions,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
} from "../../components/FormField";
import { MediaImageField } from "../../components/MediaPickerDialog";
import { showSuccess, showError, showSeoWarnings } from "../../lib/toast";
import { emptyBg, moveItem } from "../templates/shared";
import { generateUUID } from "../../commonjquery/CommonJquery";
import {
  server_post_data,
  solution_page_save_update,
  get_solution_page_data,
} from "../../serviceconnection/ServiceConnection";
import { SEOSection } from "../../components/SEOSection";
import { ICON_OPTIONS } from "../../lib/iconsData";
const CATEGORIES = [
  { value: "industry", label: "Industry" },
  { value: "role", label: "Role" },
  { value: "technology", label: "Technology" },
];

function BulletRepeater({ bullets, setBullets, label = "Point" }) {
  return (
    <div className="space-y-2">
      {bullets.map((b, i) => (
        <div key={i} className="flex gap-2">
          <TextInput
            value={b.title}
            onChange={(e) =>
              setBullets(
                bullets.map((x, idx) => (idx === i ? e.target.value : x)),
              )
            }
            placeholder={`${label} ${i + 1}`}
            className="flex-1"
          />
          <ReorderButtons
            index={i}
            total={bullets.length}
            onMove={(f, t) => setBullets(moveItem(bullets, f, t))}
          />
          {bullets.length > 1 && (
            <button
              type="button"
              onClick={() => setBullets(bullets.filter((_, idx) => idx !== i))}
              className="text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() => setBullets([...bullets, ""])}
        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
        style={{
          background: "hsl(var(--accent-muted))",
          color: "hsl(var(--accent))",
        }}
      >
        <Plus className="w-3.5 h-3.5" /> Add {label}
      </button>
    </div>
  );
}

function IconCardRepeater({ cards, setCards, label }) {
  return (
    <div className="pt-3 border-t border-border">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {label}
        </p>
        <button
          type="button"
          onClick={() =>
            setCards((s) => [...s, { icon: "Zap", title: "", description: "" }])
          }
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
          style={{
            background: "hsl(var(--accent-muted))",
            color: "hsl(var(--accent))",
          }}
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      {cards.map((c, i) => (
        <div
          key={i}
          className="p-3 rounded-lg border border-border space-y-3 mb-2"
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground">
              Item {i + 1}
            </p>
            <div className="flex items-center gap-2">
              <ReorderButtons
                index={i}
                total={cards.length}
                onMove={(f, t) => setCards(moveItem(cards, f, t))}
              />
              {cards.length > 1 && (
                <button
                  onClick={() =>
                    setCards((s) => s.filter((_, idx) => idx !== i))
                  }
                  className="text-destructive"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <Field label="Icon">
            <SelectInput
              value={c.icon}
              onChange={(e) =>
                setCards((s) =>
                  s.map((x, idx) =>
                    idx === i ? { ...x, icon: e.target.value } : x,
                  ),
                )
              }
              options={ICON_OPTIONS}
            />
          </Field>
          <Field label="Title">
            <TextInput
              value={c.title}
              onChange={(e) =>
                setCards((s) =>
                  s.map((x, idx) =>
                    idx === i ? { ...x, title: e.target.value } : x,
                  ),
                )
              }
              placeholder="Title"
            />
          </Field>
          <Field label="Description">
            <TextArea
              value={c.description}
              onChange={(e) =>
                setCards((s) =>
                  s.map((x, idx) =>
                    idx === i ? { ...x, description: e.target.value } : x,
                  ),
                )
              }
              placeholder="Description"
            />
          </Field>
        </div>
      ))}
    </div>
  );
}

function ProductCardRepeater({ cards, setCards, onAddCard }) {
  return (
    <div className="pt-3 border-t border-border">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Product Cards
        </p>
        <button
          type="button"
          onClick={onAddCard}
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
          style={{
            background: "hsl(var(--accent-muted))",
            color: "hsl(var(--accent))",
          }}
        >
          <Plus className="w-3.5 h-3.5" /> Add Card
        </button>
      </div>
      {cards.map((c, i) => (
        <div
          key={i}
          className="p-3 rounded-lg border border-border space-y-3 mb-2"
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground">
              Product {i + 1}
            </p>
            <div className="flex items-center gap-2">
              <ReorderButtons
                index={i}
                total={cards.length}
                onMove={(f, t) => setCards(moveItem(cards, f, t))}
              />
              {cards.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setCards((s) => s.filter((_, idx) => idx !== i))
                  }
                  className="text-destructive"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <Field label="Icon">
            <SelectInput
              value={c.icon}
              onChange={(e) =>
                setCards((s) =>
                  s.map((x, idx) =>
                    idx === i ? { ...x, icon: e.target.value } : x,
                  ),
                )
              }
              options={ICON_OPTIONS}
            />
          </Field>
          <Field label="Product Heading">
            <TextInput
              value={c.heading}
              onChange={(e) =>
                setCards((s) =>
                  s.map((x, idx) =>
                    idx === i ? { ...x, heading: e.target.value } : x,
                  ),
                )
              }
              placeholder="Product heading"
            />
          </Field>
          <Field label="Product Description">
            <TextArea
              value={c.description}
              onChange={(e) =>
                setCards((s) =>
                  s.map((x, idx) =>
                    idx === i ? { ...x, description: e.target.value } : x,
                  ),
                )
              }
              placeholder="Short description"
            />
          </Field>
          <Field label="Product URL">
            <TextInput
              value={c.url}
              onChange={(e) =>
                setCards((s) =>
                  s.map((x, idx) =>
                    idx === i ? { ...x, url: e.target.value } : x,
                  ),
                )
              }
              placeholder="https://... or /path"
            />
          </Field>
        </div>
      ))}
    </div>
  );
}

// Hero stats repeater — icon + number heading + sub-label
function HeroStatsRepeater({ stats, setStats }) {
  return (
    <div className="space-y-2">
      {stats.map((s, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="w-32 shrink-0">
            <SelectInput
              value={s.icon}
              onChange={(e) =>
                setStats(
                  stats.map((x, idx) =>
                    idx === i ? { ...x, icon: e.target.value } : x,
                  ),
                )
              }
              options={ICON_OPTIONS}
            />
          </div>
          <TextInput
            value={s.number}
            onChange={(e) =>
              setStats(
                stats.map((x, idx) =>
                  idx === i ? { ...x, number: e.target.value } : x,
                ),
              )
            }
            placeholder="e.g. 150+ Clients"
            className="flex-1"
          />
          <TextInput
            value={s.label}
            onChange={(e) =>
              setStats(
                stats.map((x, idx) =>
                  idx === i ? { ...x, label: e.target.value } : x,
                ),
              )
            }
            placeholder="e.g. Across Australia"
            className="flex-1"
          />
          <ReorderButtons
            index={i}
            total={stats.length}
            onMove={(f, t) => setStats(moveItem(stats, f, t))}
          />
          {stats.length > 1 && (
            <button
              type="button"
              onClick={() => setStats(stats.filter((_, idx) => idx !== i))}
              className="text-destructive pt-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          setStats([...stats, { icon: "Shield", number: "", label: "" }])
        }
        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
        style={{
          background: "hsl(var(--accent-muted))",
          color: "hsl(var(--accent))",
        }}
      >
        <Plus className="w-3.5 h-3.5" /> Add Stat
      </button>
    </div>
  );
}

// ─── NEW: Tech Pill Repeater — title + image ──────────────────────────────────
function TechPillRepeater({ pills, setPills }) {
  return (
    <div className="pt-3 border-t border-border">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Technology Pills
        </p>
        <button
          type="button"
          onClick={() => setPills((s) => [...s, { title: "", image: "" }])}
          className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg"
          style={{
            background: "hsl(var(--accent-muted))",
            color: "hsl(var(--accent))",
          }}
        >
          <Plus className="w-3.5 h-3.5" /> Add Pill
        </button>
      </div>
      {pills.map((p, i) => (
        <div
          key={i}
          className="p-3 rounded-lg border border-border space-y-3 mb-2"
        >
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground">
              Pill {i + 1}
            </p>
            <div className="flex items-center gap-2">
              <ReorderButtons
                index={i}
                total={pills.length}
                onMove={(f, t) => setPills(moveItem(pills, f, t))}
              />
              {pills.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setPills((s) => s.filter((_, idx) => idx !== i))
                  }
                  className="text-destructive"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <Field label="Technology Name">
            <TextInput
              value={p.title}
              onChange={(e) =>
                setPills((s) =>
                  s.map((x, idx) =>
                    idx === i ? { ...x, title: e.target.value } : x,
                  ),
                )
              }
              placeholder="e.g. SAP Business One"
            />
          </Field>
          <Field label="Technology Image">
            <MediaImageField
              label="Upload technology logo / image"
              hint="Recommended: 200×200px"
              value={p.image}
              onChange={(v) =>
                setPills((s) =>
                  s.map((x, idx) => (idx === i ? { ...x, image: v } : x)),
                )
              }
            />
          </Field>
        </div>
      ))}
    </div>
  );
}

// ─── Helper: unwrap object-array OR plain-string-array → plain string array ───
// Backend may return [{icon, title, description}] after the fix,
// or legacy plain strings before the fix. Both handled here.
function unwrapToStrings(arr) {
  if (!Array.isArray(arr) || !arr.length) return [""];
  return arr.map((item) =>
    typeof item === "string" ? item : (item.title ?? ""),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export function SolutionForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // ─── Global ───────────────────────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("technology");
  const [uuid, setUuid] = useState("");

  const [seoData, setSeoData] = useState({});
  const handleSeoChange = (field, value) =>
    setSeoData((prev) => ({ ...prev, [field]: value }));

  // ─── HERO ─────────────────────────────────────────────────────────────────
  const [heroHeading, setHeroHeading] = useState("");
  const [heroHighlight, setHeroHighlight] = useState("");
  const [heroSub, setHeroSub] = useState("");
  const [heroBg, setHeroBg] = useState(emptyBg());
  const [heroBgImageUrl, setHeroBgImageUrl] = useState("");
  const [heroPrimary, setHeroPrimary] = useState({ text: "", url: "" });
  const [heroSecondary, setHeroSecondary] = useState({ text: "", url: "" });
  const [deliverBullets, setDeliverBullets] = useState([""]);
  const [heroStats, setHeroStats] = useState([
    { icon: "Shield", number: "", label: "" },
  ]);

  // ─── SNAPSHOT ─────────────────────────────────────────────────────────────
  const [snapActive, setSnapActive] = useState(true);
  const [snapHeading, setSnapHeading] = useState("");
  const [snapDesc, setSnapDesc] = useState("");
  const [snapImageUrl, setSnapImageUrl] = useState("");
  const [snapImageAlt, setSnapImageAlt] = useState("");
  const [snapCta, setSnapCta] = useState({ text: "", url: "" });

  // ─── TEMPLATE: BY TECHNOLOGY only ────────────────────────────────────────
  const emptyProdCard = () => ({
    icon: "BarChart2",
    heading: "",
    description: "",
    url: "",
  });

  const [prodActive, setProdActive] = useState(true);
  const [prodHeading, setProdHeading] = useState("");
  const [prodCards, setProdCards] = useState([emptyProdCard()]);

  const [capActive, setCapActive] = useState(true);
  const [capHeading, setCapHeading] = useState("");
  const [capCards, setCapCards] = useState([
    { icon: "Zap", title: "", description: "" },
  ]);

  const [compActive, setCompActive] = useState(true);
  const [compHeading, setCompHeading] = useState("");
  const [compBullets, setCompBullets] = useState([""]);
  const [compSideImageUrl, setCompSideImageUrl] = useState("");
  const [compSideImageAlt, setCompSideImageAlt] = useState("");

  const [whoActive, setWhoActive] = useState(true);
  const [whyActive, setWhyActive] = useState(true);
  const [whoHeading, setWhoHeading] = useState("");
  const [whyHeading, setWhyHeading] = useState("");
  const [whoLeftBullets, setWhoLeftBullets] = useState([""]);
  const [whoRightBullets, setWhoRightBullets] = useState([""]);

  // ─── TEMPLATE: BY INDUSTRY & BY ROLE ─────────────────────────────────────
  const [chalActive, setChalActive] = useState(true);
  const [chalHeading, setChalHeading] = useState("");
  const [chalList, setChalList] = useState([""]);
  const [solveList, setSolveList] = useState([""]);
  const [chalCta, setChalCta] = useState({ text: "", url: "" });

  const [techActive, setTechActive] = useState(true);
  const [techHeading, setTechHeading] = useState("");
  const [techSub, setTechSub] = useState("");
  // ── CHANGED: state is now [{title, image}] instead of [""] ──
  const [techPills, setTechPills] = useState([{ title: "", image: "" }]);
  const [techCta, setTechCta] = useState({ text: "", url: "" });

  const [helpActive, setHelpActive] = useState(true);
  const [helpHeading, setHelpHeading] = useState("");
  const [helpSub, setHelpSub] = useState("");
  const [helpCards, setHelpCards] = useState([
    { icon: "Zap", title: "", description: "" },
  ]);
  const [helpCta, setHelpCta] = useState({ text: "", url: "" });

  // ─── FINAL CTA ────────────────────────────────────────────────────────────
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState("");
  const [ctaDesc, setCtaDesc] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState({ text: "", url: "" });
  const [ctaSecondary, setCtaSecondary] = useState({ text: "", url: "" });

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));
  const updateBg = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  useEffect(() => {
    if (mode === "edit" && id) {
      const fetchExisting = async () => {
        try {
          const formData = new FormData();
          formData.append("main_id", id);
          const response = await server_post_data(
            get_solution_page_data,
            formData,
          );
          let existing = response.data?.data ?? null;
          if (!existing) return;
          console.log("Fetched existing solution data:", existing);

          // ── GLOBAL ──────────────────────────────────────────────────────
          setTitle(existing.solution_name ?? "");
          setCategory(existing.solution_category ?? "Technology");
          setUuid(existing.uuid ?? generateUUID());

          // ── HERO ────────────────────────────────────────────────────────
          setHeroHeading(existing.banner_heading ?? "");
          setHeroHighlight(existing.banner_highlight_word ?? "");
          setHeroSub(existing.banner_subheading ?? "");
          setHeroBgImageUrl(existing.banner_bg_image ?? "");
          setHeroPrimary({
            text: existing.banner_primary_cta_text ?? "",
            url: existing.banner_primary_cta_link ?? "",
          });
          setHeroSecondary({
            text: existing.banner_secondary_cta_text ?? "",
            url: existing.banner_secondary_cta_link ?? "",
          });
          setDeliverBullets(
            existing.banner_deliver_points?.length
              ? existing.banner_deliver_points
              : [""],
          );
          setHeroStats(
            existing.banner_stats?.length
              ? existing.banner_stats.map((s) => ({
                  icon: s.icon ?? "Shield",
                  number: s.number ?? "",
                  label: s.label ?? "",
                }))
              : [{ icon: "Shield", number: "", label: "" }],
          );

          // ── SNAPSHOT ────────────────────────────────────────────────────
          setSnapActive(existing.show_snapshot ?? true);
          setSnapHeading(existing.snapshot_heading ?? "");
          setSnapDesc(existing.snapshot_description ?? "");
          setSnapImageUrl(existing.snapshot_right_image ?? "");
          setSnapImageAlt(existing.snapshot_alt_image ?? "");
          setSnapCta({
            text: existing.snapshot_btn_text ?? "",
            url: existing.snapshot_btn_link ?? "",
          });

          // ── BY TECHNOLOGY — Related Products ────────────────────────────
          setProdActive(existing.show_featured_items ?? true);
          setProdHeading(existing.featured_items_heading ?? "");
          setProdCards(
            existing.featured_items?.length
              ? existing.featured_items.map((c) => ({
                  icon: c.icon_url ?? "BarChart2",
                  heading: c.heading ?? "",
                  description: c.description ?? "",
                  url: c.learn_more_link ?? "",
                }))
              : [{ icon: "BarChart2", heading: "", description: "", url: "" }],
          );

          // ── BY TECHNOLOGY — Key Capabilities ────────────────────────────
          setCapActive(existing.show_capabilities ?? true);
          setCapHeading(existing.capabilities_heading ?? "");
          setCapCards(
            existing.capabilities_cards?.length
              ? existing.capabilities_cards
              : [{ icon: "Zap", title: "", description: "" }],
          );

          // ── BY TECHNOLOGY — Comprehensive Solutions ──────────────────────
          setCompActive(existing.show_deliver_section ?? true);
          setCompHeading(existing.deliver_heading ?? "");
          // ── unwrap: backend may return [{icon,title,description}] or ["string"] ──
          setCompBullets(existing.deliver_points);

          setCompSideImageUrl(existing.deliver_right_image ?? "");
          setCompSideImageAlt(existing.deliver_alt_image ?? "");

          // ── BY TECHNOLOGY — Who It's For ─────────────────────────────────
          setWhoActive(existing.show_who_its_for_section ?? true);
          setWhoHeading(existing.who_its_for_heading ?? "");
          // ── unwrap ──
          setWhoLeftBullets(existing.who_its_for_points);

          // ── BY TECHNOLOGY — Why It's Needed ──────────────────────────────
          setWhyActive(existing.show_why_section ?? true);
          setWhyHeading(existing.why_heading ?? "");
          // ── unwrap ──
          setWhoRightBullets(existing.why_points);

          // ── BY INDUSTRY & ROLE — Challenges & Solutions ──────────────────
          setChalActive(existing.show_challenges_section ?? true);
          setChalHeading(existing.challenges_heading ?? "");
          // ── unwrap ──
          setChalList(existing.challenge_rows);
          setSolveList(existing.solve_rows);
          setChalCta({
            text: existing.challenges_cta_text ?? "",
            url: existing.challenges_cta_link ?? "",
          });

          // ── BY INDUSTRY & ROLE — Technology Stack ────────────────────────
          setTechActive(existing.show_tech_stack_section ?? true);
          setTechHeading(existing.tech_stack_heading ?? "");
          setTechSub(existing.tech_stack_subheading ?? "");
          // ── CHANGED: populate as [{title, image}] ──
          const rawPills = existing.tech_stack_pills ?? [];
          setTechPills(
            rawPills.length
              ? rawPills.map((p) =>
                  typeof p === "string"
                    ? { title: p, image: "" }
                    : { title: p.title ?? "", image: p.image ?? "" },
                )
              : [{ title: "", image: "" }],
          );
          setTechCta({
            text: existing.tech_stack_cta_text ?? "",
            url: existing.tech_stack_cta_link ?? "",
          });

          // ── BY INDUSTRY & ROLE — How We Help ─────────────────────────────
          setHelpActive(existing.show_how_we_help_section ?? true);
          setHelpHeading(existing.how_we_help_heading ?? "");
          setHelpSub(existing.how_we_help_subheading ?? "");
          setHelpCards(
            existing.how_we_help_cards?.length
              ? existing.how_we_help_cards
              : [{ icon: "Zap", title: "", description: "" }],
          );
          setHelpCta({
            text: existing.how_we_help_cta_text ?? "",
            url: existing.how_we_help_cta_link ?? "",
          });

          // ── FINAL CTA ────────────────────────────────────────────────────
          setCtaActive(existing.show_cta_section ?? true);
          setCtaHeading(existing.cta_heading ?? "");
          setCtaDesc(existing.cta_description ?? "");
          setCtaPrimary({
            text: existing.cta_primary_text ?? "",
            url: existing.cta_primary_link ?? "",
          });
          setCtaSecondary({
            text: existing.cta_secondary_text ?? "",
            url: existing.cta_secondary_link ?? "",
          });

          // ── SEO ──────────────────────────────────────────────────────────
          const sd = existing.seo_data ?? {};
          setSeoData({
            seo_id: sd.seo_id ?? 0,
            title_name: sd.title_name ?? "",
            description: sd.description ?? "",
            image: sd.image ?? "",
            actual_url: sd.actual_url ?? "",
            link_url: sd.link_url ?? "",
            favicon: sd.favicon ?? "",
            keywords: sd.keywords ?? "",
            changefreq: sd.changefreq ?? "",
            priority_screen: sd.priority ?? "0.8",
          });
        } catch (err) {
          console.error("Failed to load solution:", err);
          showError("Failed to load solution data. Please try again.");
        }
      };
      fetchExisting();
    }
  }, [mode, id]);

  // ─── Template flags ───────────────────────────────────────────────────────
  const isTech = category.toLowerCase() === "technology";
  const isIndustry = category.toLowerCase() === "industry";
  const isRole = category.toLowerCase() === "role";
  const isIndustryOrRole = isIndustry || isRole;

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required";
    return e;
  };

  // ─── Helper: wrap plain strings → icon card objects ───────────────────────
  const wrapToIconCards = (arr) =>
    arr
      .filter(Boolean)
      .map((item) => ({ icon: "Zap", title: item, description: "" }));

  const buildPayload = (saveStatus) => {
    const localId = mode === "edit" ? Number(id) : Date.now();
    const uuId = mode === "edit" ? (uuid ?? generateUUID()) : generateUUID();

    const payload = {
      // ── GLOBAL ────────────────────────────────────────────────────────────
      p_id: mode === "edit" ? localId : 0,
      uuid: uuId,
      solution_name: title,
      solution_category: category,

      // ── HERO ──────────────────────────────────────────────────────────────
      banner_heading: heroHeading,
      banner_highlight_word: heroHighlight,
      banner_subheading: heroSub,
      banner_bg_image: heroBgImageUrl,
      banner_bg_alt: heroBg.bgImageAlt,
      banner_primary_cta_text: heroPrimary.text,
      banner_primary_cta_link: heroPrimary.url,
      banner_secondary_cta_text: heroSecondary.text,
      banner_secondary_cta_link: heroSecondary.url,
      banner_deliver_points: deliverBullets
        .filter(Boolean)
        .map((t) => ({ icon: "", title: t, description: "" })),
      // ── CHANGED: include title & description keys to match expected shape ──
      banner_stats: heroStats
        .filter((s) => s.number || s.label)
        .map((s) => ({
          icon: s.icon,
          number: s.number,
          label: s.label,
        })),

      // ── SNAPSHOT ──────────────────────────────────────────────────────────
      show_snapshot: snapActive,
      snapshot_heading: snapHeading,
      snapshot_description: snapDesc,
      snapshot_btn_text: snapCta.text,
      snapshot_btn_link: snapCta.url,
      snapshot_right_image: snapImageUrl,
      snapshot_alt_image: snapImageAlt,

      // ── FINAL CTA ─────────────────────────────────────────────────────────
      show_cta_section: ctaActive,
      cta_heading: ctaHeading,
      cta_description: ctaDesc,
      cta_primary_text: ctaPrimary.text,
      cta_primary_link: ctaPrimary.url,
      cta_secondary_text: ctaSecondary.text,
      cta_secondary_link: ctaSecondary.url,

      // ── SEO ───────────────────────────────────────────────────────────────
      title_name: seoData.title_name ?? "",
      description: seoData.description ?? "",
      image: seoData.image ?? "",
      favicon: seoData.favicon ?? "",
      actual_url: seoData.actual_url ?? "",
      link_url: seoData.link_url ?? "",
      keywords: seoData.keywords ?? "",
      changefreq: seoData.changefreq ?? "",
      priority: seoData.priority_screen ?? "0.8",
    };

    // ── BY TECHNOLOGY only ─────────────────────────────────────────────────
    Object.assign(payload, {
      show_featured_items: isTech ? prodActive : false,
      featured_items_heading: isTech ? prodHeading : "",
      featured_items: isTech
        ? prodCards.map((c) => ({
            type: "product",
            icon_url: c.icon,
            heading: c.heading,
            description: c.description,
            learn_more_link: c.url,
          }))
        : [],

      show_capabilities: isTech ? capActive : false,
      capabilities_heading: isTech ? capHeading : "",
      capabilities_cards: isTech
        ? capCards.map((c) => ({
            icon: c.icon,
            title: c.title,
            description: c.description,
          }))
        : [],

      show_deliver_section: isTech ? compActive : false,
      deliver_heading: isTech ? compHeading : "",
      // ── CHANGED: wrap plain strings → [{icon, title, description}] ──
      deliver_points: isTech ? wrapToIconCards(compBullets) : [],
      deliver_right_image: isTech ? compSideImageUrl : "",
      deliver_alt_image: isTech ? compSideImageAlt : "",

      show_who_its_for_section: isTech ? whoActive : false,
      who_its_for_heading: isTech ? whoHeading : "",
      // ── CHANGED: wrap plain strings → [{icon, title, description}] ──
      who_its_for_points: isTech ? wrapToIconCards(whoLeftBullets) : [],

      show_why_section: isTech ? whyActive : false,
      why_heading: isTech ? whyHeading : "",
      // ── CHANGED: wrap plain strings → [{icon, title, description}] ──
      why_points: isTech ? wrapToIconCards(whoRightBullets) : [],
    });

    // ── BY INDUSTRY & BY ROLE ──────────────────────────────────────────────
    Object.assign(payload, {
      show_challenges_section: isIndustryOrRole ? chalActive : false,
      challenges_heading: isIndustryOrRole ? chalHeading : "",
      // ── CHANGED: wrap plain strings → [{icon, title, description}] ──
      challenge_rows: isIndustryOrRole ? wrapToIconCards(chalList) : [],
      solve_rows: isIndustryOrRole ? wrapToIconCards(solveList) : [],
      challenges_cta_text: isIndustryOrRole ? chalCta.text : "",
      challenges_cta_link: isIndustryOrRole ? chalCta.url : "",

      show_tech_stack_section: isIndustryOrRole ? techActive : false,
      tech_stack_heading: isIndustryOrRole ? techHeading : "",
      tech_stack_subheading: isIndustryOrRole ? techSub : "",
      // ── CHANGED: [{title, image}] → [{icon, title, description, image}] ──
      tech_stack_pills: isIndustryOrRole
        ? techPills
            .filter((p) => p.title)
            .map((p) => ({
              icon: "Zap",
              title: p.title,
              description: "",
              image: p.image ?? "",
            }))
        : [],
      tech_stack_cta_text: isIndustryOrRole ? techCta.text : "",
      tech_stack_cta_link: isIndustryOrRole ? techCta.url : "",

      show_how_we_help_section: isIndustryOrRole ? helpActive : false,
      how_we_help_heading: isIndustryOrRole ? helpHeading : "",
      how_we_help_subheading: isIndustryOrRole ? helpSub : "",
      how_we_help_cards: isIndustryOrRole
        ? helpCards.map((c) => ({
            icon: c.icon,
            title: c.title,
            description: c.description,
          }))
        : [],
      how_we_help_cta_text: isIndustryOrRole ? helpCta.text : "",
      how_we_help_cta_link: isIndustryOrRole ? helpCta.url : "",
    });

    return payload;
  };

  // ─── Save handler ─────────────────────────────────────────────────────────
  const handleSave = async (saveStatus) => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      showError("Please fix the errors before saving.");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const payload = buildPayload(saveStatus);
      console.log("Payload to save:", payload);

      const formData = new FormData();
      // Object.entries(payload).forEach(([key, value]) => {
      //   if (typeof value === "boolean") {
      //     formData.append(key, value ? 1 : 0);
      //   } else if (typeof value === "object") {
      //     formData.append(key, JSON.stringify(value));
      //   } else {
      //     formData.append(key, value);
      //   }
      // });

      const saveRes = await server_post_data(solution_page_save_update, payload);

      showSuccess(
        mode === "create" ? "Solution created!" : "Solution updated!",
      );
      showSeoWarnings(saveRes);
      navigate("/solutions");
    } catch (err) {
      console.error(err);
      showError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/solutions")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              {mode === "create" ? "Create Solution" : "Edit Solution"}
            </h1>
            <p className="page-subtitle">
              Category: <strong>{category}</strong> —{" "}
              {isTech
                ? "Template: By Technology"
                : isIndustry
                  ? "Template: By Industry"
                  : "Template: By Role"}
            </p>
          </div>
        </div>
      </div>

      {/* Basic Details */}
      <div className="section-panel">
        <div className="section-panel-header">
          <h2 className="section-panel-title">Basic Details</h2>
        </div>
        <div className="section-panel-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Solution Title" required error={errors.title}>
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Solution title"
                error={!!errors.title}
              />
            </Field>
            <Field label="Category">
              <SelectInput
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                options={CATEGORIES}
              />
            </Field>
          </div>
        </div>
      </div>

      {/* ===================== HERO (all templates) ===================== */}
      <CollapsibleSection title="Hero Section" active={true}>
        <Field label="Main Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
            placeholder="Main hero headline"
          />
        </Field>
        <Field label="Highlight Word">
          <TextInput
            value={heroHighlight}
            onChange={(e) => setHeroHighlight(e.target.value)}
            placeholder="Word to highlight"
          />
        </Field>
        <Field label="Subheading">
          <TextArea
            value={heroSub}
            onChange={(e) => setHeroSub(e.target.value)}
            placeholder="Supporting description"
          />
        </Field>
        <Field label="Background Image">
          <MediaImageField
            label="Select hero background"
            hint="Recommended: 1920×1080px"
            value={heroBgImageUrl}
            onChange={setHeroBgImageUrl}
            alt={heroBg.bgImageAlt}
            onAltChange={(v) => updateBg(setHeroBg, "bgImageAlt", v)}
          />
        </Field>
        {isIndustryOrRole && (
          <div className="pt-3 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              "What We Deliver" Panel
            </p>
            <Field label="Bullet Points">
              <BulletRepeater
                bullets={deliverBullets}
                setBullets={setDeliverBullets}
                label="Bullet"
              />
            </Field>
          </div>
        )}
        {/* CTA Buttons */}
        <div className="pt-3 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Buttons
          </p>
          <CTAGroup
            label="Primary Button"
            text={heroPrimary.text}
            url={heroPrimary.url}
            newTab={heroPrimary.newTab}
            onTextChange={(v) => updateBtn(setHeroPrimary, "text", v)}
            onUrlChange={(v) => updateBtn(setHeroPrimary, "url", v)}
          />
          <CTAGroup
            label="Secondary Button"
            text={heroSecondary.text}
            url={heroSecondary.url}
            newTab={heroSecondary.newTab}
            onTextChange={(v) => updateBtn(setHeroSecondary, "text", v)}
            onUrlChange={(v) => updateBtn(setHeroSecondary, "url", v)}
          />
        </div>
        {/* Stats Row */}
        <div className="pt-3 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
            Stats Row
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            Icon · Heading (e.g. "150+ Clients") · Sub-label (e.g. "Across
            Australia")
          </p>
          <HeroStatsRepeater stats={heroStats} setStats={setHeroStats} />
        </div>
      </CollapsibleSection>

      {/* ===================== SNAPSHOT (all templates) ===================== */}
      <CollapsibleSection
        title="Snapshot Section"
        active={snapActive}
        onToggleActive={setSnapActive}
      >
        <Field label="Heading">
          <TextInput
            value={snapHeading}
            onChange={(e) => setSnapHeading(e.target.value)}
            placeholder="Section heading"
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={snapDesc}
            onChange={(e) => setSnapDesc(e.target.value)}
            placeholder="Description text"
          />
        </Field>
        <Field label="Side Image">
          <MediaImageField
            label="Upload snapshot image"
            hint="800×600px"
            value={snapImageUrl}
            onChange={setSnapImageUrl}
            alt={snapImageAlt}
            onAltChange={setSnapImageAlt}
          />
        </Field>
        <CTAGroup
          label="CTA Button"
          text={snapCta.text}
          url={snapCta.url}
          newTab={snapCta.newTab}
          onTextChange={(v) => updateBtn(setSnapCta, "text", v)}
          onUrlChange={(v) => updateBtn(setSnapCta, "url", v)}
        />
      </CollapsibleSection>

      {/* ===================== TEMPLATE: BY TECHNOLOGY ===================== */}
      {isTech && (
        <>
          <CollapsibleSection
            title="Related Products Section"
            active={prodActive}
            onToggleActive={setProdActive}
          >
            <Field label="Heading">
              <TextInput
                value={prodHeading}
                onChange={(e) => setProdHeading(e.target.value)}
                placeholder="Related Products"
              />
            </Field>
            <ProductCardRepeater
              cards={prodCards}
              setCards={setProdCards}
              onAddCard={() => setProdCards((s) => [...s, emptyProdCard()])}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Key Capabilities Section"
            active={capActive}
            onToggleActive={setCapActive}
          >
            <Field label="Heading">
              <TextInput
                value={capHeading}
                onChange={(e) => setCapHeading(e.target.value)}
                placeholder="Key Capabilities"
              />
            </Field>
            <IconCardRepeater
              cards={capCards}
              setCards={setCapCards}
              label="Capability Cards"
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Comprehensive Solutions Section"
            active={compActive}
            onToggleActive={setCompActive}
          >
            <Field label="Heading">
              <TextInput
                value={compHeading}
                onChange={(e) => setCompHeading(e.target.value)}
                placeholder="Comprehensive Solutions"
              />
            </Field>
            <Field label="Bullet Points">
              <BulletRepeater
                bullets={compBullets}
                setBullets={setCompBullets}
                label="Bullet"
              />
            </Field>
            <Field label="Side Image">
              <MediaImageField
                label="Upload side image"
                value={compSideImageUrl}
                onChange={setCompSideImageUrl}
                alt={compSideImageAlt}
                onAltChange={setCompSideImageAlt}
              />
            </Field>
          </CollapsibleSection>

          <CollapsibleSection
            title="Who It's For Section"
            active={whoActive}
            onToggleActive={setWhoActive}
          >
            <Field label="Heading">
              <TextInput
                value={whoHeading}
                onChange={(e) => setWhoHeading(e.target.value)}
                placeholder="Who Benefits"
              />
            </Field>
            <div className="mt-2">
              <Field label="Bullet Points">
                <BulletRepeater
                  bullets={whoLeftBullets}
                  setBullets={setWhoLeftBullets}
                  label="Bullet"
                />
              </Field>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Why It's Needed Section"
            active={whyActive}
            onToggleActive={setWhyActive}
          >
            <Field label="Heading">
              <TextInput
                value={whyHeading}
                onChange={(e) => setWhyHeading(e.target.value)}
                placeholder="Why It's Needed"
              />
            </Field>
            <div className="mt-2">
              <Field label="Bullet Points">
                <BulletRepeater
                  bullets={whoRightBullets}
                  setBullets={setWhoRightBullets}
                  label="Bullet"
                />
              </Field>
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* ===================== TEMPLATE: BY INDUSTRY & BY ROLE ===================== */}
      {isIndustryOrRole && (
        <>
          <CollapsibleSection
            title="Challenges & Solutions Section"
            active={chalActive}
            onToggleActive={setChalActive}
          >
            <Field label="Heading">
              <TextInput
                value={chalHeading}
                onChange={(e) => setChalHeading(e.target.value)}
                placeholder="e.g. Food & Beverage Challenges & Solutions"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-border">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Challenge Column
                </p>
                <BulletRepeater
                  bullets={chalList}
                  setBullets={setChalList}
                  label="Challenge"
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  "How We Solve It" Column
                </p>
                <BulletRepeater
                  bullets={solveList}
                  setBullets={setSolveList}
                  label="Solution"
                />
              </div>
            </div>
            <CTAGroup
              label="Learn More Button"
              text={chalCta.text}
              url={chalCta.url}
              newTab={chalCta.newTab}
              onTextChange={(v) => updateBtn(setChalCta, "text", v)}
              onUrlChange={(v) => updateBtn(setChalCta, "url", v)}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="Technology Stack Section"
            active={techActive}
            onToggleActive={setTechActive}
          >
            <Field label="Heading">
              <TextInput
                value={techHeading}
                onChange={(e) => setTechHeading(e.target.value)}
                placeholder="e.g. Technology for Food & Beverage Solutions"
              />
            </Field>
            <Field label="Subheading">
              <TextArea
                value={techSub}
                onChange={(e) => setTechSub(e.target.value)}
                placeholder="e.g. Powered by SAP Business One, Salesforce, and Power BI..."
              />
            </Field>
            {/* ── CHANGED: TechPillRepeater replaces BulletRepeater ── */}
            <TechPillRepeater pills={techPills} setPills={setTechPills} />
            <CTAGroup
              label="Explore Products Button"
              text={techCta.text}
              url={techCta.url}
              newTab={techCta.newTab}
              onTextChange={(v) => updateBtn(setTechCta, "text", v)}
              onUrlChange={(v) => updateBtn(setTechCta, "url", v)}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title="How We Help Section"
            active={helpActive}
            onToggleActive={setHelpActive}
          >
            <Field label="Heading">
              <TextInput
                value={helpHeading}
                onChange={(e) => setHelpHeading(e.target.value)}
                placeholder="e.g. How Our Solutions Help Food & Beverage Businesses"
              />
            </Field>
            <Field label="Subheading">
              <TextArea
                value={helpSub}
                onChange={(e) => setHelpSub(e.target.value)}
                placeholder="e.g. Customised for your sector's unique needs."
              />
            </Field>
            <IconCardRepeater
              cards={helpCards}
              setCards={setHelpCards}
              label="Help Cards"
            />
            <CTAGroup
              label="Request a Demo Button"
              text={helpCta.text}
              url={helpCta.url}
              newTab={helpCta.newTab}
              onTextChange={(v) => updateBtn(setHelpCta, "text", v)}
              onUrlChange={(v) => updateBtn(setHelpCta, "url", v)}
            />
          </CollapsibleSection>
        </>
      )}

      {/* ===================== FINAL CTA (all templates) ===================== */}
      <CollapsibleSection
        title="Final CTA Section"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
            placeholder="Get Started Today"
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDesc}
            onChange={(e) => setCtaDesc(e.target.value)}
            placeholder="Supporting text"
          />
        </Field>
        <CTAGroup
          label="Primary Button"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
        />
        <CTAGroup
          label="Secondary Button"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          newTab={ctaSecondary.newTab}
          onTextChange={(v) => updateBtn(setCtaSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaSecondary, "url", v)}
        />
      </CollapsibleSection>

      {/* ===================== SEO ===================== */}
      <SEOSection seoData={seoData} onChange={handleSeoChange} />

      <FormActions
        onCancel={() => navigate("/solutions")}
        onSaveDraft={() => handleSave("draft")}
        onPublish={() => handleSave("published")}
        loading={loading}
      />
    </div>
  );
}
